import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  computed,
  inject,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { AppLogoComponent } from '../../../../assets/logo/app-logo';
import { BreadcrumbService } from '../../../core/services/breadcrumb/breadcrumb.service';
import { DataService } from '../../../core/services/data/data.service';
import { DialogService, DynamicDialogRef } from '../../../core/services/dialog';
import { LocalStorageService } from '../../../core/services/local-storage/local-storage.service';
import { RoleService } from '../../../core/services/role/role.service';
import { RtoAccessService } from '../../../core/services/rto/rto-access.service';
import { UtilitiesService } from '../../../core/services/utilities/utilities.service';
import { environment } from '../../../environments/environment';
import { Breadcrumb } from '../../models/breadcrumb.model';
import { TranslatePipe } from '../../pipes/translate/translate.pipe';
import { E2eTestRunnerComponent } from '../e2e-test-runner/e2e-test-runner.component';
import { IcHomeComponent } from '../Icons/ic-home';
import { IcNotificationComponent } from '../Icons/ic-notification/ic-notification';
import { IcSearchComponent } from '../Icons/ic-search/ic-search';
import { IcTasksComponent } from '../Icons/ic-tasks/ic-tasks.component';
import { IcUserComponent } from '../Icons/ic-user/ic-user';
import { NotificationsAndAlertsComponent } from '../notifications-and-alerts/notifications-and-alerts.component';
import { loadTestAuditLogComponent } from '../test-audit-log/test-audit-log.loader';
import { UserProfilePopupComponent } from '../user-profile-popup/user-profile-popup.component';
import { BreadcrumbShipmentNavComponent } from './breadcrumb-shipment-nav/breadcrumb-shipment-nav.component';
import { GlobalSearch } from './global-search/global-search';
import { RecentDownloadsPopupComponent } from './recent-downloads-popup/recent-downloads-popup.component';

/**
 * HeaderComponent component.
 * Provides UI presentation and event handling for header component.
 */
@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    RouterModule,
    AppLogoComponent,
    IcHomeComponent,
    IcNotificationComponent,
    IcUserComponent,
    IcSearchComponent,
    TranslatePipe,
    MatTooltipModule,
    E2eTestRunnerComponent,
    IcTasksComponent,
    BreadcrumbShipmentNavComponent,
  ],
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [],
})
export class HeaderComponent implements OnDestroy {
  /**
   * Input property for declaration id.
   */
  @Input() declarationId!: string;
  /**
   * Input property for current step.
   */
  @Input() currentStep = 1;

  /** Property is dev env. */
  readonly isDevEnv = environment.env === 'local';

  /** Injected service for breadcrumb service. */
  private breadcrumbService = inject(BreadcrumbService);
  /** Injected service for data service. */
  private dataService = inject(DataService);
  /** Injected service for dialog service. */
  private dialogService = inject(DialogService);
  /** Injected service for local storage service. */
  private localStorageService = inject(LocalStorageService);
  /** Injected service for utilities. */
  private utilities = inject(UtilitiesService);
  /** Injected service for router. */
  private router = inject(Router);
  /** Injected service for role. */
  private role = inject(RoleService);
  /** Injected service for rto access service. */
  private rtoAccessService = inject(RtoAccessService);
  /** Property dialog ref. */
  private dialogRef: DynamicDialogRef | undefined | null;

  // Get breadcrumb items from BreadcrumbService
  /** Property breadcrumb items. */
  breadcrumbItems = this.breadcrumbService.breadcrumbItems;

  /** Search input text */
  public searchText = '';

  // Helper method to close any open dialog
  /**
   * Handles close current dialog logic.
   */
  private closeCurrentDialog() {
    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = null;
    }
  }

  // Compute page title always from the current URL / active module so that
  // the large header title consistently shows the module name regardless of
  // which tab, detail page, or breadcrumb trail is active.
  /** Property page title. */
  pageTitle = computed(() => {
    // Trigger reactivity on breadcrumb updates
    this.breadcrumbItems();
    const url = this.router.url;
    if (url.includes('/import/')) {
      if (url.includes('shipment-errors')) return 'shipmentError';
      if (url.includes('declaration-errors')) return 'declarationError';
      if (url.includes('shipments')) return 'importShipments';
      if (url.includes('declarations')) return 'importDeclarations';
    }
    if (url.includes('/export/')) {
      if (url.includes('shipment-errors')) return 'shipmentError';
      if (url.includes('declaration-errors')) return 'declarationError';
      if (url.includes('shipments')) return 'exportShipments';
      if (url.includes('declarations')) return 'exportDeclarations';
    }
    if (url.includes('/transit-declarations/')) {
      return 'transitNcts';
    }
    if (url.includes('/declarations/')) {
      if (url.includes('/alerts')) return 'declarationError';
      return 'declarations';
    }
    if (url.startsWith('/transit-declarations')) return 'transitNcts';
    if (url.startsWith('/declarations')) return 'declarations';

    // Must run before the generic `/shipments/` match — RTO lives under `/rto/shipments/…`.
    if (url.includes('/rto') || url.startsWith('/rto')) return 'pgts';

    if (url.includes('/shipments/')) {
      if (url.includes('/alerts')) return 'shipmentError';
      return 'shipments';
    }
    if (url.startsWith('/shipments')) return 'shipments';
    if (url.includes('/tickets/') || url.startsWith('/tickets')) return 'tasks';
    if (url.includes('/dashboard')) return 'home';
    if (url.startsWith('/email')) return 'emailLogs';
    if (url.startsWith('/commodity-classifier')) return 'commodityClassifier';
    if (url.includes('/emcs') || url.startsWith('/emcs')) return 'emcs';
    if (url.includes('/settings')) {
      // Extract the first settings sub-module segment (e.g. '/settings/brokers/...' → 'brokers')
      const settingsMatch = url.match(/\/settings\/([a-z-]+)/);
      if (settingsMatch) {
        const subModule = settingsMatch[1];
        // Nested manifest create/edit under a declaration template stays in Templates.
        if (
          subModule === 'manifest-templates' &&
          this.router.parseUrl(this.router.url).queryParams[
            'declarationTemplateId'
          ]
        ) {
          return 'templates';
        }
        // Convert kebab-case segment to camelCase translation key
        const keyMap: Record<string, string> = {
          'manifest-templates': 'manifestTemplates',
          'declaration-templates': 'templates',
          'download-templates': 'downloadTemplates',
        };
        return keyMap[subModule] ?? subModule;
      }
      return 'globalModules';
    }
    // Fall back to activeModule signal
    const active = this.dataService.activeModule();
    if (active.includes('shipment')) return 'shipments';
    if (active.includes('declaration')) return 'declarations';
    if (active.includes('ticket') || active === 'tasks') return 'tasks';
    if (active.includes('settings') || active === 'globalModules')
      return 'globalModules';
    return active || 'home';
  });

  // The first item is always 'home' icon root
  /** Property root breadcrumb. */
  rootBreadcrumb = computed(() => {
    const items = this.breadcrumbItems();
    return items.length > 0 ? items[0] : null;
  });

  // Breadcrumbs following the home root
  /** Property remaining breadcrumbs. */
  remainingBreadcrumbs = computed(() => {
    const items = this.breadcrumbItems();
    return items.slice(1);
  });

  /** Property is shipment details page. */
  isShipmentDetailsPage = computed(() => {
    this.breadcrumbItems();
    return /\/shipments\/details\//.test(this.router.url.split('?')[0]);
  });

  /**
   * Handles show shipment breadcrumb nav logic.
   * @param item - The item
   * @param index - The index
   */
  showShipmentBreadcrumbNav(item: Breadcrumb, index: number): boolean {
    if (!this.isShipmentDetailsPage() || !item.isDynamic) {
      return false;
    }

    const items = this.remainingBreadcrumbs();
    return index === items.length - 1;
  }

  /** Property search shortcut. */
  searchShortcut = computed(() => this.utilities.getPlatformShortcut('⌘K'));

  /**
   * Handles on search logic.
   * @param value - The value
   */
  onSearch(value: string) {}

  /**
   * Handles on profile click logic.
   */
  onProfileClick() {
    // Close any existing dialog first
    this.closeCurrentDialog();

    this.dialogRef = this.dialogService.open(UserProfilePopupComponent, {
      width: '400px',
      height: '100%',
      baseZIndex: 10000,
      dismissableMask: true,
      modal: true,
      closeOnEscape: true,
      closable: true,
      styleClass: 'profile-dialog custom-header border-none!',
      maskStyleClass: 'profile-mask-right',
    });

    if (this.dialogRef) {
      this.dialogRef.onClose.subscribe((result) => {
        if (result?.action === 'logout') {
          this.onLogout();
        } else if (result?.action === 'changePassword') {
          this.onChangePassword();
        }
      });
    }
  }

  /**
   * Handles on notification click logic.
   */
  onNotificationClick() {
    // Close any existing dialog first
    this.closeCurrentDialog();

    this.dialogRef = this.dialogService.open(NotificationsAndAlertsComponent, {
      data: { activeTab: 'notifications' },
      width: '400px',
      height: '100%',
      baseZIndex: 10000,
      dismissableMask: true,
      modal: true,
      closeOnEscape: true,
      closable: true,
      styleClass: 'profile-dialog custom-header border-none!',
      maskStyleClass: 'profile-mask-right',
    });
  }

  /**
   * Handles on alert click logic.
   */
  onAlertClick() {
    this.closeCurrentDialog();

    this.dialogRef = this.dialogService.open(NotificationsAndAlertsComponent, {
      data: { activeTab: 'alerts' },
      width: '400px',
      height: '100%',
      baseZIndex: 10000,
      dismissableMask: true,
      modal: true,
      closeOnEscape: true,
      closable: true,
      styleClass: 'profile-dialog custom-header border-none!',
      maskStyleClass: 'profile-mask-right',
    });
  }

  /**
   * Handles on recent downloads click logic.
   */
  onRecentDownloadsClick() {
    this.closeCurrentDialog();

    this.dialogRef = this.dialogService.open(RecentDownloadsPopupComponent, {
      width: '400px',
      height: '100%',
      baseZIndex: 10000,
      dismissableMask: true,
      modal: true,
      closeOnEscape: true,
      closable: true,
      styleClass: 'profile-dialog custom-header border-none!',
      maskStyleClass: 'profile-mask-right',
    });
  }

  /**
   * Opens the local-only declaration test-audit dialog.
   */
  async openTestAuditLog(): Promise<void> {
    if (environment.env !== 'local') {
      return;
    }
    this.closeCurrentDialog();
    const TestAuditLogComponent = await loadTestAuditLogComponent();
    if (!TestAuditLogComponent) {
      return;
    }
    this.dialogRef = this.dialogService.open(TestAuditLogComponent, {
      header: '',
      showHeader: false,
      width: '1400px',
      height: '90dvh',
      contentStyle: {
        padding: '0',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      },
      styleClass: 'action-dialog custom-header',
      dismissableMask: true,
      modal: true,
      closable: true,
    });
  }

  /**
   * Handles on global search click logic.
   */
  onGlobalSearchClick() {
    // Close any existing dialog first
    this.closeCurrentDialog();

    this.dialogRef = this.dialogService.open(GlobalSearch, {
      width: '700px',
      height: 'auto',
      baseZIndex: 10000,
      dismissableMask: true,
      modal: true,
      closeOnEscape: true,
      closable: true,
      styleClass: 'custom-header',
      style: {
        'border-radius': '10px',
        position: 'fixed',
        top: '60px',
        left: '50%',
        transform: 'translateX(-50%)',
      },
    });
  }

  /**
   * Handles on logout logic.
   */
  onLogout() {
    this.localStorageService.signOut();
    this.router.navigate(['/auth/login']);
    this.closeCurrentDialog();
  }

  /**
   * Handles on change password logic.
   */
  onChangePassword() {
    this.router.navigate(['/auth/change-password']);
    this.closeCurrentDialog();
  }

  /**
   * Lifecycle hook that cleans up subscriptions on component destruction.
   */
  ngOnDestroy() {
    this.closeCurrentDialog();
  }

  /**
   * Handles copy declaration id logic.
   */
  copyDeclarationId() {
    navigator.clipboard.writeText(this.declarationId);
  }

  /**
   * Handles clear search logic.
   */
  public clearSearch(): void {
    this.searchText = ''; // Clear the search text
  }

  /**
   * Handles breadcrumb link click, verifying RTO access permissions.
   * @param item - Clicked breadcrumb item
   * @param event - Mouse click event
   */
  onBreadcrumbClick(item: Breadcrumb, event: MouseEvent): void {
    if (
      item.route &&
      !item.route.startsWith('/dashboard') &&
      (item.route.startsWith('/rto') || item.route.includes('rto')) &&
      !this.role.isRtoEnabledForCurrentUser()
    ) {
      event.preventDefault();
      event.stopPropagation();
      this.rtoAccessService.openRtoNotConfiguredModal();
    }
  }
}
